<!--
  TimeElement — one record_times row as a line of text: "Sep 14, 1–2:30pm".

  Read in the row's own zone, naming the zone only when it isn't the viewer's
  ("May 28, 12–1pm CT"). Where kinds mix (the aggregate Times field) the line
  leads with its kind's icon; a field that holds one kind leaves it to the
  column header. A tall grid row adds a detail line: length, seats and title
  for a row among many, weekday and distance for a single date.

  Props:
    - value: a record_times row ({ start_at, end_at, timezone, all_day, ... }).
    - showKind: lead with the kind's icon.
    - detail: show the detail line.
    - single: the field holds one time, so its detail is weekday and distance.
-->
<template>
  <span class="time-element" :class="{ 'has-detail': detail }" :title="tooltip">
    <span class="time-line">
      <font-awesome-icon v-if="showKind" :icon="kind.icon" class="time-kind" fixed-width />
      <span class="time-text">{{ display.text }}</span>
      <span v-if="display.zone" class="time-zone">{{ display.zone }}</span>
    </span>
    <span v-if="detail && display.detail" class="time-detail">{{ display.detail }}</span>
  </span>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { formatTime, timeKindOf, type TimeRow } from "@auger/ui/utils/times"

const props = withDefaults(
  defineProps<{
    value: TimeRow
    showKind?: boolean
    detail?: boolean
    single?: boolean
    size?: string
    fixed?: boolean
    readonly?: boolean
  }>(),
  { showKind: false, detail: false, single: false, size: "normal", fixed: false, readonly: true }
)

const display = computed(() => formatTime(props.value, { single: props.single }))
const kind = computed(() => timeKindOf(props.value))
const tooltip = computed(() => [props.value.title, kind.value.label].filter(Boolean).join(" · "))
</script>

<style>
.time-element {
  display: inline-flex;
  flex-direction: column;
  min-width: 0;
  max-width: 100%;
  font-variant-numeric: tabular-nums;

  .time-line {
    display: inline-flex;
    align-items: center;
    gap: 0.4em;
    min-width: 0;
    white-space: nowrap;
  }

  .time-kind {
    flex: none;
    color: var(--auger-text-weak);
    font-size: 0.9em;
  }

  .time-text {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .time-zone {
    flex: none;
    font-size: 0.85em;
    font-weight: 500;
    letter-spacing: 0.04em;
    color: var(--auger-text-weak);
  }

  .time-detail {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.9em;
    color: var(--auger-text-weak);
  }

  /* Under the text, not under the icon. */
  &:has(.time-kind) .time-detail {
    padding-left: calc(1.25em + 0.4em);
  }
}
</style>
